import React, { useMemo } from 'react';
import { useHistory } from '../../../providers/HistoryProvider';
import MoreMenu from '../../moreMenu';
import { ChevronUpDownIcon, MicrophoneIcon, PlusIcon } from '@heroicons/react/24/solid';

interface AddToVisitButtonProps {
    buttonClassName?: string;
    menuClassName?: string;
    menuItemListClassName?: string;
    menuItemClassName?: string;
    showMicIcon?: boolean;
}

export const AddToVisitButton: React.FC<AddToVisitButtonProps> = ({ 
    buttonClassName = "",
    menuClassName = "",
    menuItemListClassName = "",
    menuItemClassName = "",
    showMicIcon=false,
}: AddToVisitButtonProps) => {
    const {
        multiDocEligible,
        allNotesMetadata,
        handleShowAddNotesModalChange,
        handleAddToSession,
        handleAddToSessionNote,
        addNoteDisabledMessage,
    } = useHistory()

    const addToVisitDynamicOptions = useMemo(() => {
        return multiDocEligible ? (allNotesMetadata?.map((note, ndx) => {
            return {
                name: "Add to " + (note.title ?? `Note ${ndx.toString()}`),
                action: () => handleAddToSessionNote(note.id),
                icon: <MicrophoneIcon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-main"/>
            }
        }) ?? []) : []
    }, [multiDocEligible, allNotesMetadata, handleAddToSessionNote])

    const shouldShowBasicButton = useMemo(() => addToVisitDynamicOptions.length === 0, [addToVisitDynamicOptions])

    return shouldShowBasicButton ? <button
            data-tour-id="add-to-visit-button"
            type="button"
            title="Record additional information for this session which will be added to the notes."
            className={`whitespace-nowrap inline-flex items-center gap-x-3 rounded-md bg-main-button px-2.5 py-2.5 text-sm font-semibold text-main-button-text shadow-sm hover:bg-main-button-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 h-10 sm:h-12 overflow-hidden justify-center md:max-w-52 ${buttonClassName}`}
            onClick={() => multiDocEligible ? handleAddToSessionNote() : handleAddToSession()}
        >
            <MicrophoneIcon className={`-mr-0.5 h-5 w-5 shrink-0 block ${showMicIcon ? "" : "md:hidden"}`} aria-hidden="true" />
            <span className="hidden md:block">Add to visit</span>
        </button> : <MoreMenu 
            moreText="Add to visit" 
            height="h-10 sm:h-12"
            className={menuClassName}
            buttonClassName={`w-full sm:max-w-52 px-3.5 whitespace-nowrap !bg-main-button text-sm font-semibold text-main-button-text shadow-sm hover:!bg-main-button-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 ${buttonClassName}`}
            menuListClassName={`!bg-blue-50 divide-blue-100 !ring-blue-200 ring-2 ${menuItemListClassName}`}
            menuItemClassName={`hover:bg-blue-100 hover:text-main ${menuItemClassName}`}
            moreIcon={<div className="flex flex-row gap-x-1 items-center">
                <MicrophoneIcon className="-mr-0.5 h-5 w-5 block sm:hidden" aria-hidden="true" />
                <ChevronUpDownIcon className="-mr-0.5 h-5 w-5 block" aria-hidden="true" />
            </div>}
            options={[
                ...addToVisitDynamicOptions,
                {
                    name:"Create new note",
                    action: () => handleShowAddNotesModalChange(true),
                    icon: <PlusIcon className={`mr-3 h-5 w-5 text-gray-400 ${addNoteDisabledMessage ? "" : "group-hover:text-main"}`}/>,
                    disabled: !!addNoteDisabledMessage,
                    description: addNoteDisabledMessage,
                },   
            ]}
        />
}